import range from 'lodash/range';
import sortBy from 'lodash/sortBy';
import { Fragment, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAllInfluencerManagersQuery } from '@anytag/graphql';
import { Button, Form } from '@anytag/shared-atoms';
import { SelectorField } from '@anytag/shared-molecules';
import { Option } from '@anytag/shared-types';
import { useUserRoles } from '@anytag/auth';
import { InfluencerManagersFormValues } from './schema-types';

export const InfluencerManagersSection = () => {
  const { isAdminStaff } = useUserRoles();
  const { t } = useTranslation();
  const { data } = useAllInfluencerManagersQuery();
  const { watch, control } = useFormContext<InfluencerManagersFormValues>();
  const { remove, update } = useFieldArray<InfluencerManagersFormValues>({ control, name: 'managerIds' });

  const managerIdsValue = watch('managerIds');
  const [numOfManagers, setNumOfManagers] = useState<number>(managerIdsValue?.length || 1);

  if (!isAdminStaff) return null;

  const allInfluencerManagers = data?.allInfluencerManagers || [];
  const influencerManagerOptions: Option[] = sortBy(
    allInfluencerManagers.map((manager) => ({ label: manager.name, value: String(manager.userId) })) || [],
    'label'
  );

  return (
    <>
      <Form.SectionTitle className="mt-3 mb-0" label={t('Manager')} />

      {range(numOfManagers).map((index) => {
        const selectedMangedId = managerIdsValue?.at(index)?.id;
        const selectedManager = allInfluencerManagers.find((manager) => String(manager.userId) === selectedMangedId);
        const filteredOptions = influencerManagerOptions.filter(
          (manager) =>
            !(
              manager.value !== selectedMangedId &&
              managerIdsValue.findIndex((selectedManager) => selectedManager?.id === manager.value) !== -1
            )
        );

        return (
          <Fragment key={index}>
            <SelectorField
              options={filteredOptions}
              name={`managerIds.${index}.id`}
              onChange={(_, value) => {
                if (value) {
                  const firstEmptyIndex = managerIdsValue.findIndex((manager) => !manager?.id);
                  update(firstEmptyIndex === -1 ? index : firstEmptyIndex, { id: value as string });
                } else {
                  remove(index);
                }
              }}
            />
            {selectedManager && (
              <>
                <div className="flex text-sm">
                  <p className="basis-1/2 whitespace-nowrap">{`${t('TextForm.Email Address')}:`}</p>
                  <p className="inline-flex basis-1/2 justify-end break-all">{selectedManager.email}</p>
                </div>
                <div className="my-2 flex text-sm">
                  <p className="basis-1/2 whitespace-nowrap">{`${t('TextForm.Phone Number')}:`}</p>
                  <p className="inline-flex basis-1/2 justify-end break-all">{selectedManager.phoneNumber}</p>
                </div>
              </>
            )}
          </Fragment>
        );
      })}
      <Button
        variant="outline"
        className="mt-2"
        prefixIcon={{ icon: 'plus', size: '10px' }}
        onClick={() => setNumOfManagers((num) => num + 1)}
      />
    </>
  );
};
